/**
 * Who owns which employee field — the single statement of it.
 *
 * ── Why a registry and not `if (isHr)` at each input ──────────────────────────────────────
 *
 * The requirement is that an employee can never modify an HR-controlled field. Spread across
 * forms, that is a rule enforced in dozens of places and broken by the first person who adds an
 * input without thinking about it. Stated once, it can be read by the form (to decide what to
 * render), by the submit path (to decide what to send) and by the server (to decide what to
 * accept) — and the three cannot disagree, because there is only one list.
 *
 * The section list is also what the HR view iterates to render, so adding a section here makes
 * it appear on both sides at once rather than in one and not the other.
 *
 * ── The structural guarantee underneath it ────────────────────────────────────────────────
 *
 * This registry governs the UI. What actually makes an HR field unwritable by an employee is
 * that HR fields and employee fields live in DIFFERENT STORES — Cognito/UserProfiles versus the
 * self-service table (see types/employeeProfile.ts). The employee's write path has no access to
 * the organisational record at all. This list is the readable statement of that split, not the
 * only thing enforcing it.
 */

/** A section of the employee profile, as it appears to both the employee and HR. */
export interface ProfileSection {
  key: string;
  title: string;
  /** One line under the heading, telling the employee what the section is for. */
  hint?: string;
  /** Whether the employee fills this in. False means HR-owned and read-only on the public page. */
  employeeFillable: boolean;
  /** Section accepts file uploads in addition to its fields. */
  acceptsDocuments?: boolean;
  /** A repeating section — the employee adds rows. */
  repeating?: boolean;
}

/**
 * The sections, in the order both pages render them.
 *
 * "Employment" is first and NOT employee-fillable on purpose: the employee should see the
 * context HR has already recorded about them before being asked for anything, and seeing it
 * read-only is also how they notice HR has their designation or joining date wrong.
 */
export const PROFILE_SECTIONS: ProfileSection[] = [
  {
    key: 'employment',
    title: 'Employment details',
    hint: 'Recorded by HR. Contact HR if anything here is wrong — you cannot change it yourself.',
    employeeFillable: false,
  },
  {
    key: 'photo',
    title: 'Profile photo',
    hint: 'A clear head-and-shoulders photo. JPG or PNG.',
    employeeFillable: true,
    acceptsDocuments: true,
  },
  {
    key: 'personal',
    title: 'Personal information',
    employeeFillable: true,
  },
  {
    key: 'address',
    title: 'Address',
    hint: 'Your permanent address, and where you currently live if it differs.',
    employeeFillable: true,
  },
  {
    key: 'emergencyContact',
    title: 'Emergency contact',
    hint: 'Who we should call if something happens to you at work.',
    employeeFillable: true,
  },
  {
    key: 'qualifications',
    title: 'Qualifications',
    hint: 'Add at least one qualification, one row each, with its certificate.',
    employeeFillable: true,
    acceptsDocuments: true,
    repeating: true,
  },
  {
    key: 'previousEmployment',
    title: 'Previous employment',
    hint: 'Add a row per employer. Leave blank if this is your first job.',
    employeeFillable: true,
    acceptsDocuments: true,
    repeating: true,
  },
  {
    key: 'identifications',
    title: 'Identifications',
    hint: 'Aadhaar and PAN are needed for payroll. Others only if you have them.',
    employeeFillable: true,
    acceptsDocuments: true,
  },
  {
    key: 'family',
    title: 'Family details',
    employeeFillable: true,
    repeating: true,
  },
  {
    key: 'nominations',
    title: 'Nomination details',
    hint: 'Who receives your PF, gratuity and insurance benefits.',
    employeeFillable: true,
    repeating: true,
  },
  {
    key: 'accounts',
    title: 'Accounts & statutory',
    hint: 'Where your salary is paid, and your statutory numbers.',
    employeeFillable: true,
    acceptsDocuments: true,
  },
];

/** The sections an employee may fill in — what the public completion page renders as editable. */
export const employeeFillableSections = (): ProfileSection[] =>
  PROFILE_SECTIONS.filter((section) => section.employeeFillable);

/**
 * The top-level keys of EmployeeSelfServiceProfile an employee may write.
 *
 * The submit path filters against this rather than sending the whole object, and the server
 * applies the same list. Anything absent here — status, invitation timestamps, employeeEmail —
 * is server-owned: an employee cannot mark their own profile COMPLETED early, extend their own
 * link, or move their submission onto somebody else's record by editing the payload.
 */
export const EMPLOYEE_WRITABLE_KEYS = [
  'profilePhotoKey',
  'personal',
  'address',
  'emergencyContact',
  'qualifications',
  'previousEmployment',
  'identifications',
  'family',
  'nominations',
  'accounts',
  'documentKeys',
] as const;

export type EmployeeWritableKey = (typeof EMPLOYEE_WRITABLE_KEYS)[number];

/**
 * Strip anything the employee is not allowed to write.
 *
 * Applied on the way OUT of the browser as well as on the way in on the server. Not redundant:
 * the client copy means a bug in the form cannot silently send a field the server will reject,
 * and the failure shows up here rather than as an opaque 400 halfway through onboarding.
 */
export function pickEmployeeWritable<T extends Record<string, any>>(input: T): Partial<T> {
  const out: Partial<T> = {};
  for (const key of EMPLOYEE_WRITABLE_KEYS) {
    if (input[key] !== undefined) (out as any)[key] = input[key];
  }
  return out;
}
